import React from 'react'
import styles from './ThreePanelLayout.scss'
import { NoScrollLayout } from 'common/layouts/NoScrollLayout'
import { StandardLayout } from 'common/layouts/StandardLayout'
import { NoticeBar } from 'common/styleguide/NoticeBar'
import { ThreePanelCollapseContext } from './ThreePanelCollapseContext'
import { CONTENT_COLLAPSE_WIDTH } from 'lib/layoutConstants'
import { userIsAdmin } from 'lib/utils'
import { logErrorBoundActivation } from 'lib/sentry'
import { Msg, commonMessages } from 'lib/localization'
import _ from 'lodash'
import cx from 'classnames'

export const ROLE_SIDE_PANEL = 'ROLE_SIDE_PANEL'
export const ROLE_MAP_PANEL = 'ROLE_MAP_PANEL'
export const ROLE_BOTTOM_PANEL = 'ROLE_BOTTOM_PANEL'
export const ROLE_NOTICE_PANEL = 'ROLE_NOTICE_PANEL'

class PanelWithErrorBoundary extends React.PureComponent {
  state = { error: null }

  componentDidCatch (error, errorInfo) {
    this.setState({ error })
    // see https://sentry.io/ridewithgpscom/rwgps-ui/getting-started/javascript-react/
    // for a full explanation
    logErrorBoundActivation(error, errorInfo)
  }

  render () {
    const { error } = this.state
    const { className, ...props } = this.props
    if (error) {
      return (
        <div data-interface-spacing {...this.props} className={cx(className, styles.error)}>
          <p><Msg m={commonMessages.errorBoundary} /></p>
          <p><Msg m={commonMessages.errorBoundaryReload} values={{ a: (...c) => <a href='https://ridewithgps.zendesk.com/hc/en-us/requests/new' target='_blank'>{c}</a> }} /></p>
          {userIsAdmin && <p data-tip title='this stack trace is only visible to admins'>
            <pre style={{ overflowX: 'auto' }}><code >
              {error.stack}
            </code>
            </pre>
          </p>}
        </div>
      )
    }

    return <div {...props} className={className} />
  }
}

export class SidePanel extends React.PureComponent {
  static role = ROLE_SIDE_PANEL
  render () {
    const { className, ...props } = this.props
    return <PanelWithErrorBoundary {...props} className={cx(styles.SidePanel, className)} />
  }
}

export class BottomPanel extends React.PureComponent {
  static role = ROLE_BOTTOM_PANEL
  render () {
    const { className, height, ...props } = this.props
    return <PanelWithErrorBoundary {...props} style={{ height }} className={cx(styles.BottomPanel, className)} />
  }
}

export class MainPanel extends React.PureComponent {
  static role = ROLE_MAP_PANEL
  render () {
    const { className, height, ...props } = this.props
    return <PanelWithErrorBoundary {...props} style={{ height }} className={cx(styles.MainPanel, className)} />
  }
}

// NOTE: height of this panel (and therefore the offset of the other panels)
// is currently hardcoded to 62px (NoticeBar height with un-line-wrapped contents)
export class NoticePanel extends React.PureComponent {
  static role = ROLE_NOTICE_PANEL
  render () {
    const { className, ...props } = this.props
    return <NoticeBar {...props} className={cx(styles.NoticePanel, className)} />
  }
}

// Component that handles *layout concerns only* for our map-centric
// three-panel layout.
export class ThreePanelLayout extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      collapse: window.innerWidth < CONTENT_COLLAPSE_WIDTH
    }
  }

  componentDidMount () {
    if (window.matchMedia) {
      // fancy callback way of tracking collapse state that doesn't need debounce
      this._mediaquery = window.matchMedia(`screen and (max-width: ${CONTENT_COLLAPSE_WIDTH}px)`)
      this._mediaquery.addListener(this.onResize)
    } else {
      // dumb fallback
      window.addEventListener('resize', this.throttledOnResize)
    }
  }

  componentWillUnmount () {
    // cleanup resize event listeners
    window.removeEventListener('resize', this.throttledOnResize)
    if (this._mediaquery) {
      this._mediaquery.removeListener(this.onResize)
      delete this._mediaquery
    }
  }

  onResize = () => {
    const { collapse } = this.state
    const iw = window.innerWidth
    const shouldCollapse = iw < CONTENT_COLLAPSE_WIDTH

    if (collapse !== shouldCollapse) {
      this.setState({ collapse: shouldCollapse })
    }
  }

  throttledOnResize = _.throttle(this.onResize, 300, { trailing: true })

  childOfRole = (role) => {
    const { children } = this.props
    const childArr = React.Children.toArray(children)
    return childArr.find(c => c.type.role === role)
  }

  render () {
    const { collapse } = this.state
    const { fullscreen: _fullscreen } = this.props
    const sidePanel = this.childOfRole(ROLE_SIDE_PANEL)
    const bottomPanel = this.childOfRole(ROLE_BOTTOM_PANEL)
    const mapPanel = this.childOfRole(ROLE_MAP_PANEL)
    const noticePanel = this.childOfRole(ROLE_NOTICE_PANEL)

    const Layout = collapse
      ? StandardLayout
      : NoScrollLayout

    // only suppress sidebar in fullscreen if we're not collapsed
    const fullscreen = (!collapse && _fullscreen)

    return (
      <ThreePanelCollapseContext.Provider value={collapse}>
        <Layout>
          {noticePanel}
          <div className={cx(styles.ThreePanelLayout, collapse && styles.collapse, fullscreen && styles.fullscreen, noticePanel && styles.withNotice)}>
            {!collapse && mapPanel}
            {!fullscreen && sidePanel}
            {!collapse && bottomPanel}
          </div>
        </Layout>
      </ThreePanelCollapseContext.Provider>
    )
  }
}
